import React, { useMemo, useState } from 'react';

import classes from './LeverandorInherentRiskView.module.scss';
import { Main } from '@klp/layout';
import { LeverandorRoutes } from '@klp/leverandor/config';
import type { BransjeRisiko } from '@klp/formgenerator/services/rtkApi';
import { useGetBransjerRisikoQuery } from '@klp/formgenerator/services/rtkApi';
import i18n from '@forvalter/i18n/config';
import { sortBransjeListByValue } from '@klp/leverandor/utils';
import { Heading, ActionLink, Typography, Loader } from '@klp/design';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core';
import { RiskSelectRow } from './RiskSelectRow/RiskSelectRow';
import { Search } from '@klp/search';

export const LeverandorInherentRiskView = () => {
    const { t } = useTranslation();
    const { data: bransjeData, isLoading: bransjeDataIsLoading } =
        useGetBransjerRisikoQuery(i18n.language);

    const [filteredBransjeData, setFilteredBransjeData] = useState<
        Array<BransjeRisiko> | undefined
    >(undefined);

    const sortedBransjeData = useMemo(() => {
        if (!bransjeData) return [];
        if (!filteredBransjeData) {
            const sortedData = sortBransjeListByValue(bransjeData);
            setFilteredBransjeData(sortedData);
            return sortedData;
        }
        return filteredBransjeData;
    }, [filteredBransjeData, bransjeData]);

    const handleSearch = (searchTerm: string) => {
        if (!bransjeData) return;

        const sortedData = sortBransjeListByValue(bransjeData);

        if (!searchTerm) {
            setFilteredBransjeData(sortedData);
            return;
        }

        const filteredData = sortedData.filter((bransje) =>
            bransje.navn.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        setFilteredBransjeData(filteredData);
    };

    function createData(
        id: number,
        navn: string,
        beskrivelse: string,
        risiko: number | null,
    ) {
        return { id, navn, beskrivelse, risiko };
    }

    const rows = sortedBransjeData.map((bransje: BransjeRisiko) =>
        createData(
            bransje.id,
            bransje.navn,
            bransje.beskrivelse,
            bransje.risiko,
        ),
    );

    return (
        <Loader isLoading={bransjeDataIsLoading}>
            <Main>
                <Main.Breadcrumbs>
                    <Main.Crumb
                        id="leverandorSchema"
                        label="breadcrumbs.leverandor.schema"
                        href={LeverandorRoutes.leverandorSchema()}
                    />
                    <Main.Crumb
                        id="leverandor-inherent-risk"
                        label="leverandor.inherentRisk.breadcrumb"
                        href={LeverandorRoutes.leverandorInherentRisk()}
                    />
                </Main.Breadcrumbs>
                <Main.Navigation>
                    <ActionLink
                        actionType="linkBack"
                        translation="leverandor.backButton"
                        href={LeverandorRoutes.leverandorSchema()}
                    />
                </Main.Navigation>
                <div className={classes.pageHeader}>
                    <Heading
                        klpHeadingLevel="TWO"
                        klpColor="dark"
                        translation="leverandor.inherentRisk.title"
                    />
                    <Typography klpTypographyVariant="base">
                        {t('leverandor.inherentRisk.subtitle')}
                    </Typography>
                </div>
                <div className={classes.contentContainer}>
                    <Search
                        searchHandler={handleSearch}
                        searchOnType={true}
                        placeholder="leverandor.inherentRisk.searchPlaceholder"
                    />

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            'leverandor.inherentRisk.table.bransje',
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            'leverandor.inherentRisk.table.beskrivelse',
                                        )}
                                    </TableCell>
                                    <TableCell className={classes.riskColumn}>
                                        {t(
                                            'leverandor.inherentRisk.table.risk',
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <RiskSelectRow key={row.navn} row={row} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </Main>
        </Loader>
    );
};
